import type { ErrorLike } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'

import {
  Client,
  BufferTrackerReact as BufferTracker,
  Product,
} from '@buffer-mono/tracking-plan'

import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import type { CommentData } from '../../Comments/fragments/commentData'
import type { Service } from '../../graphqlEnums'

type TrackSuggestionUsed = {
  comment: CommentData
  suggestionText: string
}

type TrackSavedReplyUsed = {
  comment: CommentData
  savedReply: {
    id: string
    title?: string | null
    content: { text: string }
    hotkeys?: string[] | null
  }
}

// Only `id` and `service` are read for tracking; typed structurally so the
// hook accepts any producer's channel node.
type ChannelForTracking = { id: string; service: Service }

type TrackCommentsFiltered = {
  channels: ChannelForTracking[]
  filterType: string
  filterValue: string
}

type TrackCommentsSorted = {
  channels: ChannelForTracking[]
  sortType: string
  sortValue: string
}

type TrackSwitchToEngage = {
  channelIds: string[]
  channelService: Service
  cta: string
}

type TrackCommunityLoaded = {
  unansweredCommentsCount: number
  unansweredMentionsCount?: number
}

type TrackCommentReplyError = {
  apolloError: ErrorLike
  comment: CommentData
  text: string
  source?: string
  elapsedMs?: number
  label?: string
}

export type UseCommentsTracking = {
  trackSuggestionUsed: (payload: TrackSuggestionUsed) => void
  trackSavedReplyUsed: (payload: TrackSavedReplyUsed) => void
  trackCommentsFiltered: (payload: TrackCommentsFiltered) => void
  trackCommentsSorted: (payload: TrackCommentsSorted) => void
  trackSwitchToEngage: (payload: TrackSwitchToEngage) => void
  trackCommunityLoaded: (payload: TrackCommunityLoaded) => void
  trackCommentReplyError: (payload: TrackCommentReplyError) => void
}

export const useCommentsTracking = (): UseCommentsTracking => {
  const commonTrackingProperties = useCommonTrackingProperties()

  const trackSuggestionUsed = ({
    comment,
    suggestionText,
  }: TrackSuggestionUsed): void => {
    BufferTracker.suggestionUsed({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      channelId: comment.channelId,
      serviceType: comment.serviceType,
      postId: comment.post.id,
      commentId: comment.id,
      commentText: comment.text,
      suggestionContent: suggestionText,
      ...commonTrackingProperties,
    })
  }

  const trackSavedReplyUsed = ({
    comment,
    savedReply,
  }: TrackSavedReplyUsed): void => {
    BufferTracker.savedReplyUsed({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      channelId: comment.channelId,
      serviceType: comment.serviceType,
      postId: comment.post.id,
      commentId: comment.id,
      savedReplyId: savedReply.id,
      savedReplyName: savedReply.title,
      savedReplyContent: savedReply.content.text,
      savedReplyHotkeys: savedReply.hotkeys?.join(',') ?? '',
      ...commonTrackingProperties,
    })
  }

  const trackCommentsFiltered = ({
    channels,
    filterType,
    filterValue,
  }: TrackCommentsFiltered): void => {
    const channelId = channels.length === 1 ? channels[0].id : 'all'
    const serviceType = channels.length === 1 ? channels[0].service : 'all'

    BufferTracker.commentsFiltered({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      channelId,
      serviceType,
      filterType,
      filterValue,
      ...commonTrackingProperties,
    })
  }

  const trackCommentsSorted = ({
    channels,
    sortType,
    sortValue,
  }: TrackCommentsSorted): void => {
    const channelId = channels.length === 1 ? channels[0].id : 'all'
    const serviceType = channels.length === 1 ? channels[0].service : 'all'

    BufferTracker.commentsSorted({
      clientName: Client.PublishWeb,
      product: Product.Publish,
      channelId,
      serviceType,
      sortType,
      sortValue,
      ...commonTrackingProperties,
    })
  }

  const trackSwitchToEngage = ({
    channelIds,
    channelService,
    cta,
  }: TrackSwitchToEngage): void => {
    const channelId = channelIds.length === 1 ? channelIds[0] : 'all'
    const serviceType = channelIds.length === 1 ? channelService : 'all'

    BufferTracker.switchEngage({
      clientName: Client.PublishWeb,
      channelId,
      serviceType,
      cta,
      ...commonTrackingProperties,
    })
  }

  const trackCommunityLoaded = ({
    unansweredCommentsCount,
    unansweredMentionsCount,
  }: TrackCommunityLoaded): void => {
    BufferTracker.communityLoaded({
      clientName: Client.PublishWeb,
      unansweredCommentsCount,
      ...(unansweredMentionsCount !== undefined
        ? { unansweredMentionsCount }
        : {}),
      ...commonTrackingProperties,
    })
  }

  const trackCommentReplyError = ({
    apolloError,
    comment,
    text,
    source,
    elapsedMs,
    label,
  }: TrackCommentReplyError): void => {
    try {
      let errorContext: string
      if (elapsedMs !== undefined) {
        errorContext = JSON.stringify({
          elapsedMs,
          timestamp: new Date().toISOString(),
        })
      } else if (label !== undefined) {
        errorContext = label
      } else {
        const graphQLErrors = CombinedGraphQLErrors.is(apolloError)
          ? apolloError.errors.map((err) => ({
              message: err.message,
              extensions: err.extensions,
              path: err.path,
            }))
          : []

        // When the error isn't a combined GraphQL error, treat it as the
        // network-level failure.
        const networkError = CombinedGraphQLErrors.is(apolloError)
          ? null
          : {
              message: apolloError.message,
              name: apolloError.name,
            }

        errorContext = JSON.stringify({
          graphQLErrors,
          networkError,
        })
      }

      BufferTracker.commentReplyFailed({
        clientName: Client.PublishWeb,
        channelId: comment.channelId,
        commentId: comment.id,
        postId: comment.post?.id ?? '',
        serviceType: comment.serviceType,
        errorMessage: apolloError.message,
        errorSource: source ?? 'frontend',
        errorContext,
        hasText: Boolean(text),
        hasMedia: false,
        ...commonTrackingProperties,
      })
    } catch {
      // Tracking failures must not affect the user-facing reply flow
    }
  }

  return {
    trackSuggestionUsed,
    trackSavedReplyUsed,
    trackCommentsFiltered,
    trackCommentsSorted,
    trackSwitchToEngage,
    trackCommunityLoaded,
    trackCommentReplyError,
  }
}
